import { useEffect } from 'react';
import { Flex, Skeleton } from 'antd';
import { useStore } from '../../../helpers/use-store';
import { useNotification } from '../../../hooks/use-notification';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

declare global {
  interface Window {
    onInstagramAuthSuccess?: (data: { code: string; state: string }) => void;
    onInstagramAuthError?: (error: string) => void;
  }
}

export default function InstagramCallback() {
  const {
    dataStore: { socialAccountStore },
  } = useStore();
  const notificationApi = useNotification();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onSuccess = async (data: { code: string; state: string }) => {
    try {
      const response = await socialAccountStore.instagramAuth(data);

      if (response) {
        notificationApi.success({
          placement: 'top',
          icon: '',
          message: t('common.success'),
          description: t('socialAccounts.instagram.addedSuccess'),
        });
      } else {
        onError();
      }
    } catch (e) {
      console.error(`failed instagram auth`, e);
    }
  };

  const onError = () => {
    notificationApi.error({
      placement: 'top',
      icon: '',
      message: t('common.error'),
      description: t('socialAccounts.instagram.addError'),
    });
  };

  useEffect(() => {
    const handleAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state') || '';
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      // Define popup success/error functions
      const popupSuccess = (data: { code: string; state: string }) => {
        window.opener.onInstagramAuthSuccess?.(data);
        window.close();
      };

      const popupError = (error: string) => {
        window.opener.onInstagramAuthError?.(error);
        window.close();
      };

      // Define redirect success/error functions
      const redirectSuccess = async (data: { code: string; state: string }) => {
        await onSuccess(data);
        navigate('/videos');
      };

      const redirectError = () => {
        onError();
        navigate('/videos');
      };

      // Assign final handlers based on window.opener
      const handleSuccess = window.opener ? popupSuccess : redirectSuccess;
      const handleError = window.opener ? popupError : redirectError;

      try {
        if (error || !code) {
          handleError(errorDescription || 'Authentication failed');
        } else {
          await handleSuccess({ code, state });
        }
      } catch (error) {
        handleError('Failed to connect Instagram account');
      }
    };

    handleAuth();
  }, []);

  return (
    <Flex
      style={{ width: '100%', height: '100%', justifyContent: 'center' }}
      align={'center'}
    >
      <Skeleton
        style={{ height: '100%' }}
        paragraph={{ rows: 10 }}
        active={true}
      />
    </Flex>
  );
}
